'use client';

import { Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import { SplashScreen } from '@packages/ui';
import React from 'react';
import * as Sentry from '@sentry/nextjs';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  maxRetryCount?: number; // Maximum retry attempts
  isRefreshComponentOnly?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  showError: boolean;
  retryCount: number;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, showError: false, retryCount: 0 };
  }

  static getDerivedStateFromError() {
    // Update state to indicate an error has occurred
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error);
    if (
      this.props.isRefreshComponentOnly &&
      this.props.maxRetryCount &&
      this.state.retryCount < this.props.maxRetryCount
    ) {
      setTimeout(() => this.retry, 2000);
      return;
    }

    const reloadTimestamp = localStorage.getItem('reload_timestamp');
    // Convert the timestamp to Date
    const reloadDate = new Date(parseInt(reloadTimestamp ?? '0'));

    // If the successful reload timestamp is within the last 30s, then do not reload and show the error instead
    const showError = !!reloadTimestamp && Date.now() - reloadDate.getTime() < 30000;

    console.error('Uncaught error:', showError, error, errorInfo);
    this.setState({ hasError: true, showError, error, errorInfo });

    if (!showError) {
      // Refresh the page after an error occurs and we do not want to show error yet
      localStorage.setItem('reload_timestamp', Date.now().toString());
      window.location.reload();
    }
  }

  retry = () => {
    const showError = this.state.retryCount >= (this.props.maxRetryCount ?? 0);
    this.setState((prevState) => ({
      ...prevState,
      showError,
      hasError: showError,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render() {
    // Has error and show error because it's unrecoverable
    if (this.state.hasError && this.state.showError) {
      return <ErrorInfo {...this.state} />;
    }

    // Has error and not showing error as we want to reload to recover
    if (this.state.hasError) {
      if (this.props.isRefreshComponentOnly) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '100px' }}>
            <CircularProgress color='inherit' size={20} />
          </Box>
        );
      }
      return <SplashScreen />;
    }

    return this.props.children;
  }
}

const ErrorInfo = ({ error, errorInfo }: ErrorBoundaryState) => {
  return (
    <Box
      component='main'
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexGrow: 1,
        py: '80px',
      }}
    >
      <Container maxWidth='lg'>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 6,
          }}
        >
          <Box
            alt='Internal server error'
            component='img'
            src='/assets/errors/error-500.png'
            sx={{
              height: 'auto',
              maxWidth: '100%',
              width: 400,
            }}
          />
        </Box>
        <Typography align='center' variant={'h4'}>
          500: Internal Server Error
        </Typography>
        <Typography align='center' color='text.secondary' sx={{ mt: 0.5 }}>
          {error?.message}
        </Typography>
        <Typography align='center' color='text.secondary' sx={{ mt: 1 }}>
          Ops! Something went wrong. We are sorry that this happened, please inform Zolo adminstrator
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 6,
            mb: 2,
          }}
        >
          <Button type='submit' variant='contained' color='primary' href='/'>
            Back To Home
          </Button>
        </Box>

        <Typography align='center' color='text.secondary' sx={{ mt: 0.5 }}>
          {errorInfo?.componentStack}
        </Typography>
      </Container>
    </Box>
  );
};

export default ErrorBoundary;
